import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Box, Button, LinearProgress } from "@mui/material";
import { AddCircle } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { getItems } from "../../api/items";
import { DataGrid, DataGridOptions } from "../../components/datagrid/DataGrid";
import { RecordsContext, RecordsContextType } from "../../components/records/Records";
import { RecordsHeader } from "../../components/recordsHeader/RecordsHeader";
import { useStore } from "../../store/store";
import { GAP_ALLOCATOR_ENTITY, GAP_PRIORITY_ENTITY, gapsCollection } from "./gapsOptions";
import { useGapsFields } from "./useGapsFields";

const DAY_DIVIDER_ROW_CLASS = "gaps-row-new-day";

type GapsListProps = {
	separator?: React.ReactNode;
};

export const GapsList = ({ separator }: GapsListProps) => {
	const paginationModel = useStore((state) => state.gridSettings.paginationModel);
	const { formatedFields, status: fieldsStatus } = useGapsFields();

	const { data, status, refetch } = useQuery({
		queryKey: [`${GAP_ALLOCATOR_ENTITY}-records-${paginationModel.page}-${paginationModel.pageSize}`],
		queryFn: () =>
			getItems(GAP_ALLOCATOR_ENTITY, "*", {
				page: paginationModel.page,
				pageSize: paginationModel.pageSize,
				withRowCount: true
			})
	});

	const gapsDataGridOptions: DataGridOptions = useMemo(() => {
		const items = (data?.items ?? []) as Array<{ id: string; day: string }>;
		const firstRowIdsOfEachDay = new Set(
			items.filter((item, index) => index > 0 && items[index - 1].day !== item.day).map((item) => item.id)
		);

		const options: DataGridOptions = {
			navigateToDetail: () => ".",
			getRowClassName: (params) => (firstRowIdsOfEachDay.has(params.row.id) ? DAY_DIVIDER_ROW_CLASS : "")
		};
		return options;
	}, [data]);

	if (status === "loading" || fieldsStatus === "loading") return <LinearProgress color="secondary" />;
	if (status === "error" || fieldsStatus === "error") return <div>Erro ao carregar os gaps</div>;

	const recordsContext = {
		entity: GAP_ALLOCATOR_ENTITY,
		items: data?.items ?? [],
		formatedFields,
		collection: gapsCollection,
		type: "list" as const,
		metadata: {
			total_count: data?.metadata?.total_count,
			filter_count: data?.metadata?.filter_count
		},
		refetch
	} as RecordsContextType;

	return (
		<RecordsContext.Provider value={recordsContext}>
			<Box
				display="flex"
				flexDirection="column"
				sx={{
					[`& .${DAY_DIVIDER_ROW_CLASS}`]: { borderTop: "2px solid #b0bec5" }
				}}
			>
				<RecordsHeader
					collection={gapsCollection}
					hideCreateButton
					hideFiltersButton
					hideSettingsButton
					optionalComponents={{
						leftOfButtons: (
							<Button component={Link} to={`/${GAP_PRIORITY_ENTITY}/create`} startIcon={<AddCircle />}>
								Criar
							</Button>
						)
					}}
				/>
				{separator}
				<DataGrid
					paginationModel={paginationModel}
					heightOfHeaders={244}
					type="records"
					options={gapsDataGridOptions}
				/>
			</Box>
		</RecordsContext.Provider>
	);
};
